<template>
  <div class="account-container top-filter-dropdown-container">
      <div class="top-filter-dropdown-group">
        <transition name="slide">
            <div class="top-filter-dropdown-list" v-if="show">
                <div class="title">
                    {{ titleLabel }}
                </div>
                <div class="top-filter-dropdown-div">
                  <el-input
                    v-model="searchText"
                    v-alphanumeric-validation
                    maxlength="50"
                    clearable
                    @change="search"
                    class="inline-input"
                    size="mini"
                    suffix-icon="el-icon-search"
                    id="search-account-list"
                    ref="search-account-list"
                    ></el-input>
                </div>
                <div class="list"
                    infinite-scroll-immediate
                    infinite-scroll-distance="100"
                    v-infinite-scroll="search"
                    :infinite-scroll-disabled="busy">
                    <div v-bind:key="account.id" v-for="account in accountList" class="list-item">
                       <span class="span-check">
                            <el-radio v-model="userAccountNumber" :label="account.number" @change="change(account)">
                                {{"  "}}
                            </el-radio>
                        </span>
                        <span class="span-name">
                            {{ account.name }} - {{ account.number }}
                        </span>
                    </div>
                    <span class="account-not-found" v-if="showNotFound"> {{ $t('accountComponent.noAccountsFound') }} </span>
                </div>
                <p class="loading" v-if="accountLoading"><img src="@/assets/svg/loading.svg" alt="loading"/></p>
                <p class="scroll-ellipsis" v-if="!noMore && !accountLoading"> ... </p>
            </div>
        </transition>
        <div class="top-filter-dropdown-selected">
            <span class="top-filter-dropdown-label"> {{ accountLabel }} </span>
            <span class="top-filter-dropdown-change">
                <el-button icon="fa fa-random"  size="mini" circle @click="openList" v-if="isMultiple"></el-button>
            </span>
        </div>
      </div>
    <div class="component-container-overlay"  v-if="show"></div>
</div>
</template>

<script>
import { Loading } from 'element-ui';
import { mapGetters } from 'vuex';
import constants from '@/utils/constants';

export default {
  name: 'accountList',
  props: {
    /* An array of vuex actions to be called when an account is selected.
     * These actions will be called orderd by position, in waterfall mode.
     * Can contain strings or another arrays. If a given position have a string
     * an action with the string value will be invoked. If a given position
     * have an array, the first position of the internal array will be used
     * as action name. The rest of positions will be used as action parameters.
     */
    actionStrSelectChained: {
      type: Array,
      default: null
    },
    actionStrMountChained: {
      type: Array,
      default: null
    },
    accountType: {
      type: Number,
      default: null
    },
    handling: {// This prop indicates if the accounts to search are handled by the current user account.
      type: Boolean,
      default: false,
      required: false
    },
    children: {// This prop indicates if the search of accounts need to include the children of the current user account.
      type: Boolean,
      default: false,
      required: false
    },
    lockSelection: {
      type: Boolean,
      default: false
    },
    includeInactive: {
      type: Boolean,
      default: false
    },
    includeUserInstructions: {
      type: Boolean,
      default: false,
      required: false
    }
  },
  data() {
    return {
      show: false,
      showNotFound: false,
      busy: false,
      searchText: '',
      allAccounts: constants.ACCOUNTS.TYPE[0].value,
      shipperType: constants.ACCOUNTS.TYPE[1].value,
      consigneeType: constants.ACCOUNTS.TYPE[2].value,
      thirdPartyBillToType: constants.ACCOUNTS.TYPE[3].value,
    };
  },
  computed: {
    ...mapGetters([
      'shippers',
      'consignees',
      'accounts',
      'user',
      'orderEntry'
    ]),
    noMore() {
      return this.accounts.list.length >= this.accounts.totalRows;
    },
    disabled() {
      return this.accounts.loading || this.noMore;
    },
    titleLabel() {
      if (this.accountType === this.shipperType) {
        return this.$t('accountComponent.selectShipper');
      }
      if (this.accountType === this.consigneeType) {
        return this.$t('accountComponent.selectConsignee');
      }
      if (this.accountType === this.thirdPartyBillToType) {
        return this.$t('accountComponent.selectThirdPartyBillToAccount');
      }
      return this.$t('accountComponent.selectAccount');
    },
    accountLabel() {
      return this.user.accountLabel;
    },
    accountList() {
      return this.accounts.list;
    },
    accountLoading() {
      return this.accounts.loading;
    },
    isMultiple() {
      return !this.lockSelection && this.accounts.multipleAccounts && this.user.accountNumber;
      // return !this.lockSelection && this.user.accountNumber;
    },
    accountSearch() {
      return this.accounts.filters.search;
    },
    userAccountType() {
      return this.user.accountType;
    },
    userAccountNumber: {
      get() {
        return this.user.accountNumber;
      },
      set(val) {
        this.user.accountNumber = val;
      }
    }
  },
  methods: {
    async search(resetSearch) {
      this.busy = true;
      // If sarch text is changed or reset search is mandatory we call to resetShipperList
      if ((this.searchText !== this.accountSearch) || resetSearch) {
        await this.$store.dispatch('account/resetList', this.accountType);
      }
      await this.$store.dispatch('account/getListScroll',
        {
          search: this.searchText, accountType: this.accountType, includeInactive: this.includeInactive, handling: this.handling, children: this.children
        });
      this.busy = false;
    },
    async change(accountSelected) {
      if (this.lockSelection) { return; }
      this.$emit('account-selected', accountSelected);
      this.$store.dispatch('setUserAccount', { account: accountSelected, accountType: this.accountType });
      if (this.includeUserInstructions) {
        const instructions = { deliveryInstructions: accountSelected.deliveryInstructions || '', alarmInstructions: accountSelected.alarmInstructions || '' };
        await this.$store.dispatch('setUserInstructions', instructions);
      }
      const loading = Loading.service(constants.LOADING.DEFAULT_CONFIG);
      await this.callSelectAction();
      loading.close();
      this.show = false;
    },
    openList() {
      if (this.lockSelection) { return; }
      this.show = !this.show;
      // Its necessary nextTick because we need to wait the component render.
      if (this.show) {
        this.$nextTick(() => {
          this.$refs['search-account-list'].focus();
        });
      }
    },
    async callSelectAction() {
      if (this.actionStrSelectChained) {
        for (const action of this.actionStrSelectChained) {
          if (Array.isArray(action) && action.length > 0) {
            const actionName = action.slice(0, 1).toString();
            const actionParams = action.slice(1);
            await this.$store.dispatch(actionName, ...actionParams);
          } else {
            await this.$store.dispatch(action);
          }
        }
      }
    },
    async callMountAction() {
      if (this.actionStrMountChained) {
        for (const action of this.actionStrMountChained) {
          if (Array.isArray(action) && action.length > 0) {
            const actionName = action.slice(0, 1).toString();
            const actionParams = action.slice(1);
            await this.$store.dispatch(actionName, ...actionParams);
          } else {
            await this.$store.dispatch(action);
          }
        }
      }
    }
  },
  async mounted() {
    this.callMountAction();
    const resetSearch = true;
    // If account selected is same type of the component needs.
    if (this.userAccountNumber && (this.userAccountType === this.accountType || this.allAccounts === this.accountType)) {
      this.callSelectAction();
      // Call reset search but without loading because the account is already selected, and no need await.
      this.search(resetSearch);
    } else {
      this.$store.dispatch('resetUserAccount');
      const loading = Loading.service(constants.LOADING.DEFAULT_CONFIG);
      await this.search(resetSearch);

      // SHIPPERS and CONSIGNEES (and CUSTOMERS etc) Users Normal Flow: If the list of accounts only returns 1, then we select it by default.
      if (this.accountList && this.accountList.length === 1) {
        this.change(this.accountList[0]);
        this.$emit('accountReady');
        this.show = false;
      } else {
        this.show = true;
        this.showNotFound = !this.accountList.length;
      }
      loading.close();
    }

    // Its necessary nextTick because we need to wait the component render.
    if (this.show) {
      this.$nextTick(() => {
        this.$refs['search-account-list'].focus();
      });
    }
    if (this.lockSelection) {
      this.show = false;
      this.showNotFound = false;
    }
    this.$emit('accountReady');
  }
};
</script>
